import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDownNarrowWide, ArrowUpNarrowWide, ExternalLink, Loader2, Star } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/useIsMobile";

import { StarRating } from "@/components/ui/StarRating";
import { Button } from "@/components/ui/button";
import { EventsErrorState } from "@/components/events/EventsErrorState";
import { EventsPagination } from "@/components/events/EventsPagination";
import { StatusBadge } from "@/components/events/StatusBadge";
import { bulkSetPriority } from "@/lib/eventsApi";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import {
  type PriorityReviewPreset,
  usePriorityReview,
} from "@/hooks/usePriorityReview";
import { usePriorityReviewFilters } from "@/hooks/usePriorityReviewFilters";
import type { EventListItem } from "@cdt/shared";

const PRESETS: { value: PriorityReviewPreset; label: string }[] = [
  { value: "this_week", label: "Questa settimana" },
  { value: "next_week", label: "Prossima settimana" },
  { value: "all", label: "Tutti" },
];

export function PriorityReviewPage() {
  const {
    preset,
    page,
    pageSize,
    sortOrder,
    setPreset,
    setPage,
    setPageSize,
    setSortOrder,
  } = usePriorityReviewFilters();
  const query = usePriorityReview({ preset, page, pageSize, sortOrder });
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = query.data?.totalPages ?? 0;
  const isInitialLoading = query.isLoading && query.data === undefined;

  // Local overrides for pending star ratings (before save)
  const [pendingPriorities, setPendingPriorities] = useState<
    Record<string, 0 | 1 | 2 | 3>
  >({});
  const [focusedIdx, setFocusedIdx] = useState(0);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  // Reset pending/focus whenever the visible item set changes (preset, page
  // or sort order all change which events are shown).
  useEffect(() => {
    setPendingPriorities({});
    setFocusedIdx(0);
  }, [preset, page, sortOrder]);

  // If the current page ends up out of range (e.g. after saving, rated
  // events leave the priority=0 filter and the total shrinks), snap back to
  // the last valid page instead of showing an empty page indefinitely.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages, setPage]);

  const queryClient = useQueryClient();
  const saveMutation = useMutation({
    mutationFn: async (updates: Record<string, 0 | 1 | 2 | 3>) => {
      const grouped: Record<number, string[]> = {};
      for (const [id, p] of Object.entries(updates)) {
        if (!grouped[p]) grouped[p] = [];
        grouped[p].push(id);
      }
      await Promise.all(
        Object.entries(grouped).map(([p, ids]) =>
          bulkSetPriority(ids, Number(p)),
        ),
      );
    },
    onSuccess: () => {
      setPendingPriorities({});
      void queryClient.invalidateQueries({ queryKey: ["events"] });
      void queryClient.invalidateQueries({ queryKey: ["priority-review"] });
      void queryClient.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Priorità salvate");
    },
    onError: () => {
      toast.error("Errore nel salvataggio delle priorità");
    },
  });

  const pendingCount = Object.keys(pendingPriorities).length;
  const isMobile = useIsMobile();

  function setPriority(id: string, value: 0 | 1 | 2 | 3) {
    setPendingPriorities((prev) => ({ ...prev, [id]: value }));
  }

  function saveAll() {
    if (pendingCount === 0) return;
    saveMutation.mutate(pendingPriorities);
  }

  // Keyboard navigation: j/k move focus, 0-3 set priority for focused row
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIdx((i) => Math.min(i + 1, items.length - 1));
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIdx((i) => Math.max(i - 1, 0));
      } else if (["0", "1", "2", "3"].includes(e.key)) {
        e.preventDefault();
        const item = items[focusedIdx];
        if (item) {
          setPriority(item.id, Number(e.key) as 0 | 1 | 2 | 3);
        }
      } else if (e.key === "Enter") {
        saveAll();
      }
    },
    [items, focusedIdx],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Scroll focused row into view
  useEffect(() => {
    const item = items[focusedIdx];
    if (item) {
      rowRefs.current[item.id]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [focusedIdx, items]);

  return (
    <div className="container max-w-5xl space-y-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Star className="h-6 w-6 text-amber-400" />
            Review priorità
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Eventi creati ancora senza valutazione. Usa{" "}
            <kbd className="rounded border px-1 py-0.5 text-xs font-mono">j</kbd>
            /
            <kbd className="rounded border px-1 py-0.5 text-xs font-mono">k</kbd>{" "}
            per navigare,{" "}
            <kbd className="rounded border px-1 py-0.5 text-xs font-mono">0-3</kbd>{" "}
            per votare,{" "}
            <kbd className="rounded border px-1 py-0.5 text-xs font-mono">Enter</kbd>{" "}
            per salvare.
          </p>
        </div>
        {pendingCount > 0 && (
          <button
            type="button"
            onClick={saveAll}
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-70"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Salva {pendingCount} modifica{pendingCount !== 1 ? "he" : ""}
          </button>
        )}
      </div>

      {/* Preset tabs + sort toggle */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b">
        <div className="flex gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPreset(p.value)}
              className={cn(
                "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
                preset === p.value
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mb-1 gap-1.5 text-muted-foreground"
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
        >
          {sortOrder === "asc" ? (
            <ArrowUpNarrowWide className="h-3.5 w-3.5" />
          ) : (
            <ArrowDownNarrowWide className="h-3.5 w-3.5" />
          )}
          {sortOrder === "asc" ? "Più vecchi prima" : "Più recenti prima"}
        </Button>
      </div>

      {/* Content */}
      {isInitialLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : query.isError ? (
        <EventsErrorState
          message={
            query.error instanceof Error ? query.error.message : undefined
          }
          onRetry={() => void query.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState preset={preset} />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {total} event{total !== 1 ? "i" : "o"} non valutat
            {total !== 1 ? "i" : "o"}
          </p>

          {isMobile ? (
            /* ── Mobile card list ─────────────────────────────────────── */
            <div className="space-y-2">
              {items.map((item) => {
                const displayPriority =
                  pendingPriorities[item.id] !== undefined
                    ? (pendingPriorities[item.id] as 0 | 1 | 2 | 3)
                    : (item.priority as 0 | 1 | 2 | 3);
                const isDirty = pendingPriorities[item.id] !== undefined;
                return (
                  <div
                    key={item.id}
                    className={cn(
                      "rounded-lg border bg-card px-4 py-3",
                      isDirty && "bg-amber-50 dark:bg-amber-950/20",
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium truncate text-sm">
                        {item.title ?? (
                          <span className="text-muted-foreground">{item.emailSubject}</span>
                        )}
                      </div>
                      <a
                        href={`/events/${item.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 text-muted-foreground hover:text-foreground"
                        aria-label="Apri evento in nuova scheda"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                    {item.title && (
                      <div className="text-xs text-muted-foreground truncate">{item.emailSubject}</div>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <StatusBadge status={item.status} />
                      {item.category?.name && <span>{item.category.name}</span>}
                      {item.startDate && <span>{formatDate(item.startDate)}</span>}
                    </div>
                    <div className="mt-2">
                      <StarRating
                        value={displayPriority}
                        onChange={(v) => setPriority(item.id, v)}
                        size="sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── Desktop table ────────────────────────────────────────── */
            <div className="rounded-lg border bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 text-left w-[160px]">Data</th>
                    <th className="px-4 py-2 text-left">Titolo / Oggetto</th>
                    <th className="px-4 py-2 text-left w-[120px]">Categoria</th>
                    <th className="px-4 py-2 text-left w-[90px]">Stato</th>
                    <th className="px-4 py-2 text-left w-[110px]">Priorità</th>
                    <th className="px-4 py-2 w-[40px]" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <ReviewRow
                      key={item.id}
                      item={item}
                      isFocused={idx === focusedIdx}
                      pendingPriority={pendingPriorities[item.id]}
                      onFocus={() => setFocusedIdx(idx)}
                      onPriorityChange={(v) => setPriority(item.id, v)}
                      ref={(el) => {
                        rowRefs.current[item.id] = el;
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <EventsPagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />

          <p className="text-xs text-muted-foreground">
            Suggerimento: le modifiche non salvate sono indicate con sfondo giallo.
          </p>
        </>
      )}
    </div>
  );
}

interface ReviewRowProps {
  item: EventListItem;
  isFocused: boolean;
  pendingPriority: 0 | 1 | 2 | 3 | undefined;
  onFocus: () => void;
  onPriorityChange: (v: 0 | 1 | 2 | 3) => void;
  ref: (el: HTMLTableRowElement | null) => void;
}

function ReviewRow({
  item,
  isFocused,
  pendingPriority,
  onFocus,
  onPriorityChange,
  ref,
}: ReviewRowProps) {
  const displayPriority =
    pendingPriority !== undefined
      ? pendingPriority
      : (item.priority as 0 | 1 | 2 | 3);
  const isDirty = pendingPriority !== undefined;

  return (
    <tr
      ref={ref}
      onClick={onFocus}
      className={cn(
        "border-b transition-colors cursor-pointer",
        isFocused
          ? "bg-primary/5 ring-1 ring-inset ring-primary/30"
          : "hover:bg-muted/40",
        isDirty && "bg-amber-50 dark:bg-amber-950/20",
      )}
    >
      <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
        {formatDate(item.startDate)}
      </td>
      <td className="px-4 py-2.5">
        <div className="font-medium truncate max-w-[320px]" title={item.title ?? item.emailSubject}>
          {item.title ?? (
            <span className="text-muted-foreground">{item.emailSubject}</span>
          )}
        </div>
        {item.title && (
          <div className="text-xs text-muted-foreground truncate max-w-[320px]">
            {item.emailSubject}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-xs text-muted-foreground">
        {item.category?.name ?? "-"}
      </td>
      <td className="px-4 py-2.5">
        <StatusBadge status={item.status} />
      </td>
      <td
        className="px-4 py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <StarRating
          value={displayPriority}
          onChange={onPriorityChange}
          size="sm"
        />
      </td>
      <td
        className="px-2 py-2.5 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <a
          href={`/events/${item.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-muted-foreground hover:text-foreground"
          aria-label="Apri evento in nuova scheda"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </td>
    </tr>
  );
}

function EmptyState({ preset }: { preset: PriorityReviewPreset }) {
  const msg =
    preset === "all"
      ? "Nessun evento da valutare."
      : "Nessun evento non valutato per questo periodo.";
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
      <Star className="h-10 w-10 opacity-30" />
      <p className="text-sm">{msg}</p>
    </div>
  );
}
